<template>
  <div class="h-100">
    <Layout ref="layout">
      <template v-slot:body>
        <div>
          <h3>Biblioteca</h3>
          <div class="nav-align-top mb-4">
            <div class="tab-content">
              <div class="tab-pane fade active show">
                <div class="page-container">
                  <div class="section">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h2 class="mb-0">Arquivos</h2>
                      <button
                        class="btn btn-primary"
                        @click="carregarArquivos"
                        title="Recarregar"
                        :disabled="isLoading"
                      >
                        <i class="bx bx-refresh" :class="{ 'bx-spin': isLoading }"></i>
                      </button>
                    </div>
                    <p>Gerencie seus relatórios e arquivos armazenados.</p>

                    <!-- Loading State -->
                    <div v-if="isLoading" class="text-center py-5">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Carregando...</span>
                      </div>
                      <p class="mt-3 text-muted">Carregando arquivos...</p>
                    </div>

                    <!-- Error State -->
                    <div v-else-if="error" class="text-center py-5">
                      <i class="bx bx-error-circle text-danger" style="font-size: 3rem;"></i>
                      <p class="mt-3 text-muted">{{ error }}</p>
                      <button class="btn btn-primary" @click="carregarArquivos">
                        Tentar novamente
                      </button>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="arquivos.length === 0" class="text-center py-5">
                      <i class="bx bx-folder-open text-muted" style="font-size: 3rem;"></i>
                      <p class="mt-3 text-muted">Nenhum arquivo encontrado na biblioteca.</p>
                    </div>

                    <!-- Desktop Table View -->
                    <div v-else class="table-responsive d-none d-md-block">
                      <table class="table table-striped table-bordered">
                        <thead>
                          <tr>
                            <th style="width: 30%">Nome</th>
                            <th style="width: 20%">Propriedade</th>
                            <th style="width: 15%">Categoria</th>
                            <th style="width: 15%">Data de Criação</th>
                            <th style="width: 20%; text-align: center;">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="arquivo in arquivos" :key="arquivo.reportId">
                            <td>{{ arquivo.titulo || arquivo.reportId }}</td>
                            <td>{{ arquivo.propriedadeNome || '-' }}</td>
                            <td>
                              <span class="badge" :class="getCategoriaClass(arquivo.categoria)">
                                {{ formatCategoria(arquivo.categoria) }}
                              </span>
                            </td>
                            <td>{{ formatDate(arquivo.uploadedAt) }}</td>
                            <td class="text-center">
                              <div class="btn-group">
                                <button
                                  class="btn btn-sm btn-outline-info"
                                  @click="abrirModalInfo(arquivo)"
                                  title="Informações"
                                >
                                  <i class="bx bx-info-circle"></i>
                                </button>
                                <button
                                  class="btn btn-sm btn-outline-success"
                                  @click="downloadArquivo(arquivo)"
                                  title="Download"
                                  :disabled="downloadingId === arquivo.reportId"
                                >
                                  <i class="bx" :class="downloadingId === arquivo.reportId ? 'bx-loader-alt bx-spin' : 'bx-download'"></i>
                                </button>
                                <button
                                  class="btn btn-sm btn-outline-danger"
                                  @click="confirmarExclusao(arquivo)"
                                  title="Excluir"
                                >
                                  <i class="bx bx-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Mobile Cards View -->
                    <div class="d-md-none">
                      <div v-for="arquivo in arquivos" :key="arquivo.reportId" class="card mb-3">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">{{ arquivo.titulo || arquivo.reportId }}</h6>
                            <span class="badge" :class="getCategoriaClass(arquivo.categoria)">
                              {{ formatCategoria(arquivo.categoria) }}
                            </span>
                          </div>

                          <div class="card-field mb-2">
                            <span class="field-label">Propriedade:</span>
                            <span class="field-value">{{ arquivo.propriedadeNome || '-' }}</span>
                          </div>

                          <div class="card-field mb-2">
                            <span class="field-label">Data de Criação:</span>
                            <span class="field-value">{{ formatDate(arquivo.uploadedAt) }}</span>
                          </div>

                          <div class="card-field mb-2" v-if="arquivo.sizeMb">
                            <span class="field-label">Tamanho:</span>
                            <span class="field-value">{{ arquivo.sizeMb.toFixed(2) }} MB</span>
                          </div>

                          <div class="action-buttons-mobile">
                            <button
                              class="btn btn-outline-info btn-mobile-action"
                              @click="abrirModalInfo(arquivo)"
                            >
                              <i class="bx bx-info-circle me-2"></i>
                              Informações
                            </button>
                            <button
                              class="btn btn-outline-success btn-mobile-action"
                              @click="downloadArquivo(arquivo)"
                              :disabled="downloadingId === arquivo.reportId"
                            >
                              <i class="bx me-2" :class="downloadingId === arquivo.reportId ? 'bx-loader-alt bx-spin' : 'bx-download'"></i>
                              {{ downloadingId === arquivo.reportId ? 'Baixando...' : 'Download' }}
                            </button>
                            <button
                              class="btn btn-outline-danger btn-mobile-action"
                              @click="confirmarExclusao(arquivo)"
                            >
                              <i class="bx bx-trash me-2"></i>
                              Excluir
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Layout>

    <!-- Modal de Informações -->
    <div class="modal fade" ref="modal_info" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header bg-primary pb-3">
            <h5 class="modal-title text-white">Informações do Arquivo</h5>
          </div>
          <div class="modal-body" v-if="arquivoSelecionado">
            <div class="info-item mb-3">
              <label class="info-label">Nome</label>
              <p class="info-value">{{ arquivoSelecionado.titulo || arquivoSelecionado.reportId }}</p>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Categoria</label>
              <p class="info-value">
                <span class="badge" :class="getCategoriaClass(arquivoSelecionado.categoria)">
                  {{ formatCategoria(arquivoSelecionado.categoria) }}
                </span>
              </p>
            </div>

            <div class="info-item mb-3" v-if="arquivoSelecionado.propriedadeNome">
              <label class="info-label">Propriedade</label>
              <p class="info-value">{{ arquivoSelecionado.propriedadeNome }}</p>
            </div>

            <div class="info-item mb-3" v-if="arquivoSelecionado.descricao">
              <label class="info-label">Descrição</label>
              <p class="info-value">{{ arquivoSelecionado.descricao }}</p>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Data de Criação</label>
              <p class="info-value">{{ formatDate(arquivoSelecionado.uploadedAt) }}</p>
            </div>

            <div class="row" v-if="arquivoSelecionado.dataHoraInicio || arquivoSelecionado.dataHoraFim">
              <div class="col-6" v-if="arquivoSelecionado.dataHoraInicio">
                <div class="info-item mb-3">
                  <label class="info-label">Período Início</label>
                  <p class="info-value">{{ formatDate(arquivoSelecionado.dataHoraInicio) }}</p>
                </div>
              </div>
              <div class="col-6" v-if="arquivoSelecionado.dataHoraFim">
                <div class="info-item mb-3">
                  <label class="info-label">Período Fim</label>
                  <p class="info-value">{{ formatDate(arquivoSelecionado.dataHoraFim) }}</p>
                </div>
              </div>
            </div>

            <div class="info-item mb-3" v-if="arquivoSelecionado.sizeMb">
              <label class="info-label">Tamanho do Arquivo</label>
              <p class="info-value">{{ arquivoSelecionado.sizeMb.toFixed(2) }} MB</p>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">ID do Relatório</label>
              <p class="info-value text-muted" style="font-size: 0.85rem;">{{ arquivoSelecionado.reportId }}</p>
            </div>
          </div>
          <div class="modal-footer modal-footer-info">
            <button type="button" class="btn btn-secondary btn-modal-action" @click="fecharModalInfo">
              Fechar
            </button>
            <button
              type="button"
              class="btn btn-success btn-modal-action"
              @click="downloadArquivo(arquivoSelecionado)"
              :disabled="downloadingId === arquivoSelecionado?.reportId"
            >
              <i class="bx me-1" :class="downloadingId === arquivoSelecionado?.reportId ? 'bx-loader-alt bx-spin' : 'bx-download'"></i>
              Download
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Exclusão -->
    <div class="modal fade" ref="modal_delete" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header bg-danger pb-3">
            <h5 class="modal-title text-white">Confirmar Exclusão</h5>
            <button type="button" class="btn-close" @click="cancelarExclusao" :disabled="isDeleting"></button>
          </div>
          <div class="modal-body" v-if="arquivoParaExcluir">
            <p>Deseja realmente excluir o arquivo "<strong>{{ arquivoParaExcluir.titulo || arquivoParaExcluir.reportId }}</strong>"?</p>
            <p class="text-muted mb-0">Esta ação não pode ser desfeita.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cancelarExclusao" :disabled="isDeleting">
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" @click="excluirArquivo" :disabled="isDeleting">
              <div
                class="spinner-border spinner-border-sm text-white me-2"
                role="status"
                v-if="isDeleting"
              >
                <span class="visually-hidden">Loading...</span>
              </div>
              {{ isDeleting ? 'Excluindo...' : 'Excluir' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <Alert ref="alert"></Alert>
  </div>
</template>

<script>
import Layout from "../../components/layout/Tema.vue";
import Alert from "../../components/Alert.vue";

export default {
  name: 'BibliotecaComponent',
  components: {
    Layout,
    Alert
  },
  data() {
    return {
      arquivos: [],
      arquivoSelecionado: null,
      arquivoParaExcluir: null,
      isLoading: false,
      isDeleting: false,
      downloadingId: null,
      error: null
    };
  },
  mounted() {
    this.carregarArquivos();
  },
  methods: {
    async carregarArquivos() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.urlGet('/Biblioteca/Listar');

        if (response.data && response.data.success) {
          this.arquivos = response.data.reports.map(report => ({
            reportId: report.reportId,
            uploadedAt: report.uploadedAt,
            titulo: report.files?.metadata?.data?.titulo || '',
            descricao: report.files?.metadata?.data?.descricao || '',
            categoria: report.files?.metadata?.data?.categoria || '',
            dataHoraInicio: report.files?.metadata?.data?.dataHoraInicio,
            dataHoraFim: report.files?.metadata?.data?.dataHoraFim,
            propriedadeNome: report.files?.metadata?.data?.propriedadeNome || '',
            sizeMb: report.files?.pdf?.sizeMb || 0,
            sizeBytes: report.files?.pdf?.sizeBytes || 0,
            pdfExists: report.files?.pdf?.exists || false,
            metadataExists: report.files?.metadata?.exists || false
          }));
        } else {
          this.error = response.data?.errorMessage || 'Erro ao carregar arquivos';
        }
      } catch (err) {
        console.error('Erro ao carregar arquivos:', err);
        this.error = 'Erro ao carregar arquivos da biblioteca';
      } finally {
        this.isLoading = false;
      }
    },

    async downloadArquivo(arquivo) {
      if (!arquivo || this.downloadingId) return;

      this.downloadingId = arquivo.reportId;

      try {
        const response = await this.urlGet(`/Biblioteca/Download/${arquivo.reportId}`);

        if (response.data && response.data.success && response.data.downloadUrl) {
          const downloadUrl = response.data.downloadUrl;
          const fileName = arquivo.titulo || arquivo.reportId;

          // Fetch do arquivo e download via blob (compatível com Safari)
          try {
            const fileResponse = await fetch(downloadUrl);
            const blob = await fileResponse.blob();

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            this.$refs.alert.show(
              "Sucesso",
              "Download iniciado",
              null,
              3000,
              "bg-success"
            );
          } catch (fetchErr) {
            // Fallback para window.open caso o fetch falhe (CORS, etc)
            console.warn('Fetch falhou, usando fallback:', fetchErr);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$refs.alert.show(
              "Sucesso",
              "Download iniciado",
              null,
              3000,
              "bg-success"
            );
          }
        } else {
          this.$refs.alert.show(
            "Erro",
            response.data?.errorMessage || "Erro ao obter link de download",
            null,
            5000,
            "bg-danger"
          );
        }
      } catch (err) {
        console.error('Erro ao fazer download:', err);
        this.$refs.alert.show(
          "Erro",
          "Erro ao fazer download do arquivo",
          null,
          5000,
          "bg-danger"
        );
      } finally {
        this.downloadingId = null;
      }
    },

    abrirModalInfo(arquivo) {
      this.arquivoSelecionado = arquivo;
      $(this.$refs.modal_info).modal({
        backdrop: "static",
        keyboard: false,
      });
    },

    fecharModalInfo() {
      $(this.$refs.modal_info).modal("hide");
      this.arquivoSelecionado = null;
    },

    confirmarExclusao(arquivo) {
      this.arquivoParaExcluir = arquivo;
      $(this.$refs.modal_delete).modal({
        backdrop: "static",
        keyboard: false,
      });
    },

    cancelarExclusao() {
      if (!this.isDeleting) {
        $(this.$refs.modal_delete).modal("hide");
        this.arquivoParaExcluir = null;
      }
    },

    async excluirArquivo() {
      if (!this.arquivoParaExcluir || this.isDeleting) return;

      this.isDeleting = true;

      try {
        const response = await this.urlDelete(`/Biblioteca/Deletar/${this.arquivoParaExcluir.reportId}`);

        if (response.data && response.data.success) {
          this.$refs.alert.show(
            "Sucesso",
            "Arquivo excluído com sucesso",
            null,
            3000,
            "bg-success"
          );

          // Remover arquivo da lista local
          this.arquivos = this.arquivos.filter(a => a.reportId !== this.arquivoParaExcluir.reportId);

          // Fechar modal de informações se estiver aberto para este arquivo
          if (this.arquivoSelecionado?.reportId === this.arquivoParaExcluir.reportId) {
            this.fecharModalInfo();
          }
        } else {
          this.$refs.alert.show(
            "Erro",
            response.data?.errorMessage || "Erro ao excluir arquivo",
            null,
            5000,
            "bg-danger"
          );
        }
      } catch (err) {
        console.error('Erro ao excluir arquivo:', err);
        this.$refs.alert.show(
          "Erro",
          "Erro ao excluir arquivo",
          null,
          5000,
          "bg-danger"
        );
      } finally {
        this.isDeleting = false;
        this.cancelarExclusao();
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateString;
      }
    },

    formatCategoria(categoria) {
      if (!categoria) return 'Sem categoria';

      const categorias = {
        'vendas': 'Vendas',
        'producao': 'Produção',
        'estoque': 'Estoque',
        'financeiro': 'Financeiro',
        'relatorio': 'Relatório',
        'analise': 'Análise'
      };

      return categorias[categoria.toLowerCase()] || categoria;
    },

    getCategoriaClass(categoria) {
      if (!categoria) return 'bg-secondary';

      const classes = {
        'vendas': 'bg-success',
        'producao': 'bg-primary',
        'estoque': 'bg-warning text-dark',
        'financeiro': 'bg-info',
        'relatorio': 'bg-secondary',
        'analise': 'bg-purple'
      };

      return classes[categoria.toLowerCase()] || 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.page-container {
  margin: 0 auto;
  padding: 20px;
}

.section {
  margin-bottom: 20px;
}

/* Table styles */
:deep(.table) {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.table th) {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  padding: 12px;
  font-weight: 600;
}

:deep(.table td) {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

:deep(.table tr:last-child td) {
  border-bottom: none;
}

/* Button styles */
:deep(.btn-primary) {
  background-color: #28a745;
  border-color: #28a745;
}

:deep(.btn-primary:hover) {
  background-color: #218838;
  border-color: #1e7e34;
}

/* Card styles for mobile */
.card {
  border: 1px solid #e3e6f0;
  border-radius: 0.5rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.1);
  transition: all 0.3s;
}

.card:hover {
  box-shadow: 0 0.25rem 2rem 0 rgba(58, 59, 69, 0.15);
}

.card-field {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.field-label {
  font-weight: 600;
  color: #5a5c69;
  font-size: 0.875rem;
}

.field-value {
  color: #3a3b45;
  font-size: 0.875rem;
}

.action-buttons-mobile {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e3e6f0;
}

.btn-mobile-action {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-mobile-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Info modal styles */
.info-label {
  display: block;
  font-weight: 600;
  color: #5a5c69;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  margin: 0;
  color: #3a3b45;
  font-size: 1rem;
}

.info-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f1f1;
}

.info-item:last-child {
  border-bottom: none;
}

/* Badge styles */
.badge {
  padding: 0.4em 0.8em;
  font-size: 0.75rem;
  font-weight: 500;
}

.bg-purple {
  background-color: #6f42c1 !important;
  color: white;
}

/* Modal styles */
.modal-backdrop {
  z-index: 1050 !important;
}

.modal {
  z-index: 1051 !important;
}


/* Responsive adjustments */
@media (max-width: 768px) {
  .page-container {
    padding: 10px 5px !important;
  }

  h3 {
    font-size: 1.25rem;
  }

  .modal-footer-info {
    flex-direction: column;
    gap: 0.5rem;
  }

  .modal-footer-info .btn-modal-action {
    width: 100%;
  }
}
</style>
